import React, { useState } from 'react';
import Dialog from './Dialog';
import Button from './Button';
import useStore from '@store';
import { Shield, Clock, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function LockDialog() {
  const { showLockDialog, closeLockDialog, lockList } = useStore();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    
    // Validate
    if (!email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      await lockList(email);
      // lockList will redirect to Stripe checkout
    } catch (error) {
      toast.error('Failed to start checkout');
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog
      isOpen={showLockDialog}
      onClose={closeLockDialog}
      title="Keep This List Forever"
      description="Upgrade to permanent storage for $1.99/month"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Current Status Warning */}
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          alignItems: 'start',
          gap: '12px'
        }}>
          <Clock style={{ width: '20px', height: '20px', color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h4 style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              marginBottom: '4px',
              color: '#92400e'
            }}>
              This list will be deleted in 6 days
            </h4>
            <p style={{ fontSize: '13px', color: '#b45309', margin: 0 }}>
              Free lists are automatically deleted after 1 week to keep your data ephemeral and private.
            </p>
          </div>
        </div>

        {/* Email Input */}
        <div>
          <label htmlFor="email" style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '8px',
            color: '#374151'
          }}>
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${errors.email ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s',
              backgroundColor: errors.email ? '#fef2f2' : 'white'
            }}
            placeholder="you@example.com"
            autoFocus
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = errors.email ? '#ef4444' : '#d1d5db'}
          />
          {errors.email && (
            <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
              {errors.email}
            </p>
          )}
        </div>
        
        {/* Features List */}
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <h4 style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '12px',
            color: '#166534',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Shield style={{ width: '16px', height: '16px' }} />
            Premium Features
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '0',
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <li style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '13px',
              color: '#166534'
            }}>
              <span style={{ color: '#059669', fontSize: '16px' }}>✓</span>
              <strong>Permanent storage</strong> - Never expires
            </li>
            <li style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '13px',
              color: '#166534'
            }}>
              <span style={{ color: '#059669', fontSize: '16px' }}>✓</span>
              <strong>Password protection option</strong> - Lock when you need it
            </li>
            <li style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '13px',
              color: '#166534'
            }}>
              <span style={{ color: '#059669', fontSize: '16px' }}>✓</span>
              Auto-cancels after 1 year of inactivity
            </li>
            <li style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '13px',
              color: '#166534'
            }}>
              <span style={{ color: '#059669', fontSize: '16px' }}>✓</span>
              Cancel anytime, no questions asked
            </li>
          </ul>
        </div>

        {/* Free vs Paid Comparison */}
        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <h5 style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>
                FREE LISTS
              </h5>
              <ul style={{ fontSize: '12px', color: '#6b7280', margin: 0, paddingLeft: '16px' }}>
                <li>7-day lifespan</li>
                <li>No passwords</li>
                <li>Perfect for quick sharing</li>
              </ul>
            </div>
            <div>
              <h5 style={{ fontSize: '12px', fontWeight: '500', color: '#059669', marginBottom: '8px' }}>
                PREMIUM LISTS
              </h5>
              <ul style={{ fontSize: '12px', color: '#059669', margin: 0, paddingLeft: '16px' }}>
                <li>Permanent storage</li>
                <li>Optional passwords</li>
                <li>Long-term collaboration</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Price */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
            $1.99
            <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/month</span>
          </div>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
            Secure payment via Stripe • Cancel anytime
          </p>
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
          <button
            type="button"
            onClick={closeLockDialog}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <Trash2 style={{ width: '14px', height: '14px' }} />
            Keep Temporary
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: isLoading ? '#9ca3af' : '#059669',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Processing...
              </>
            ) : (
              <>
                <Shield style={{ width: '16px', height: '16px' }} />
                Keep Forever
              </>
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
}